import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Space } from 'common/components/antd/Space/Space';
import { Body, Footer, Header, EnhancedModal } from 'common/components/EnhancedModal/EnhancedModal';
import { EnhancedSelect } from 'common/components/EnhancedSelect/EnhancedSelect';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { IS_BANK_FE } from 'common/constants/general-access-role.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { notifyErrorMessage, notifySuccess } from 'common/utils/notifier.utils';
import { useCreateBranchMutation } from 'domains/branches/queries/branch.query';
import {
  TCounterpartiesListResData,
  TCounterpartyEntity,
} from 'domains/counterparties/types/counterparty.types';
import { SFooter } from './modal.style';
import { EBranchesFormKeys } from '../enums/branches.enum';
import { TBranchesModalData } from '../types/branches.types';

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
  data: TBranchesModalData;
};

const CreateEditBranchModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    data: {
      counterpartiesData,
      selectedCounterparty,
    }
  } = props;
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const { mutateAsync: mutateAsyncCreateBranchMutation } = useCreateBranchMutation();

  const counterpartyOptions = ((counterpartiesData as TCounterpartiesListResData)?.counterparties || [])
    .map((counterparty: TCounterpartyEntity) => ({
      value: counterparty.guid,
      label: counterparty.name,
    }));

  useEffect(() => {
    form.setFieldsValue({
      [EBranchesFormKeys.CounterpartyGuid]: selectedCounterparty,
    });
  }, [isOpen]);

  const onCloseModalHandler = (): void => {
    form.setFields([
      {
        name: EBranchesFormKeys.Name,
        value: '',
      },
    ]);
    onCancelModalHandler();
  };

  const onSubmitHandler = async (formValues: Record<EBranchesFormKeys, string>): Promise<void> => {
    try {
      setIsLoading(true);
      const { name, counterparty_guid } = formValues;
      const requestData = {
        name,
        guid: ENV_COUNTERPARTY_GUID,
        counterparty_guid,
      };
      await mutateAsyncCreateBranchMutation({
        counterpartyGUID: ENV_COUNTERPARTY_GUID,
        requestData,
      });
      notifySuccess('usrRoleMng.branches.modal.createEdit.notification.success');
      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.BranchQueryKeys].getBranches_MultiRole._def,
      });
      setIsLoading(false);
      onCloseModalHandler();
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      notifyErrorMessage(
        error,
        'usrRoleMng.branches.modal.createEdit.notification.error',
        'usrRoleMng.branches.modal.createEdit.notification.error.duplicate',
        'duplicate',
      );
    }
  };

  return (
    <EnhancedModal open={isOpen} width={480} onCancel={onCloseModalHandler}>
      <Form
        form={form}
        onFinish={onSubmitHandler}
      >
        <Header>{t('usrRoleMng.branches.modal.createEdit.title')}</Header>
        <Body>
          <Space size={16} direction="vertical">
            <Form.Item name={EBranchesFormKeys.Name} label={t('usrRoleMng.branches.modal.createEdit.input.label.name')}>
              <Input placeholder={t('usrRoleMng.branches.modal.createEdit.inout.placeholder.name') as string} />
            </Form.Item>

            <Form.Item
              name={EBranchesFormKeys.CounterpartyGuid}
              label={t('usrRoleMng.branches.modal.create.input.counterparty.title')}
            >
              <EnhancedSelect
                style={{ width: '100%' }}
                size="large"
                placeholder={t('usrRoleMng.branches.modal.create.input.counterparty.placeholder')}
                options={counterpartyOptions}
                disabled={IS_BANK_FE}
              />
            </Form.Item>
          </Space>
        </Body>
        <Footer>
          <SFooter>
            <Button
              style={{
                marginRight: 12,
              }}
              onClick={onCloseModalHandler}
            >
              {t('usrRoleMng.branches.modal.createEdit.button.cancel')}
            </Button>
            <Button loading={isLoading} htmlType="submit" type="primary">
              {t('usrRoleMng.branches.modal.createEdit.button.create')}
            </Button>
          </SFooter>
        </Footer>
      </Form>
    </EnhancedModal>
  );
};

export default CreateEditBranchModal;
